
import React from "react";
import moment from "moment";
import withCandidate from "./withCandidate";

const TemplateBody = ({ candidate }) => {
  const languages = candidate?.languages || [];
  const skills = candidate?.skills || [];
  const work = candidate?.workExperiences || [];
  const educations = candidate?.educations || [];
  const references = candidate?.references || [];
  const interests = candidate?.interests || [];

  const formatRange = (start, end, fmt = "MMM YYYY") => {
    const s = start ? moment(start).format(fmt) : "";
    const e = end ? moment(end).format(fmt) : "Aujourd’hui";
    return s && e ? `${s} — ${e}` : s || e || "";
  };

  const guessPct = (lvl) => {
    //const L = (lvl || "").toUpperCase().trim();
    const L = 10;
    const map = { C2: 95, C1: 85, B2: 75, B1: 60, A2: 45, A1: 30 };
    return map[L] ?? 50;
  };

  return (
    <>
      <style>{`
        /********************************************************************************
         THEMES (identiques aux options du select)
        ********************************************************************************/
        .template-page {
          --accent: #e11d48;
          --ink: #111;
          --muted: #6b7280;
          --paper: #ffffff;
          --side: #f8f9fb;
          --border: #d1d5db;
          --dash: #e5e7eb;
        }

        .template-page[data-theme="blue-pro"] {
          --accent:#2379B8; --ink:#0f172a; --muted:#6b7280;
          --paper:#ffffff; --side:#f1f8ff; --border:#cbd5e1; --dash:#dbeafe;
        }
        .template-page[data-theme="slate"] {
          --accent:#64748b; --ink:#1e293b; --muted:#475569;
          --paper:#ffffff; --side:#f3f4f6; --border:#cbd5e1; --dash:#e2e8f0;
        }
        .template-page[data-theme="emerald"] {
          --accent:#10b981; --ink:#064e3b; --muted:#5f6b67;
          --paper:#ffffff; --side:#ecfdf5; --border:#bfe3d6; --dash:#c7eedf;
        }
        .template-page[data-theme="royal"] {
          --accent:#7c3aed; --ink:#1e1b4b; --muted:#6b6b9b;
          --paper:#ffffff; --side:#f5f1ff; --border:#ddd6fe; --dash:#e5ddff;
        }
        .template-page[data-theme="bordeaux"] {
          --accent:#b91c1c; --ink:#1f1f1f; --muted:#7a6565;
          --paper:#ffffff; --side:#fef2f2; --border:#fecaca; --dash:#fbd5d5;
        }
        .template-page[data-theme="noir-or"] {
          --accent:#d4a017; --ink:#e5e5e5; --muted:#cccccc;
          --paper:#1b1b1b; --side:#141414; --border:#333; --dash:#444;
        }
        .template-page[data-theme="bleu-clair"] {
          --accent:#3b82f6; --ink:#1e293b; --muted:#64748b;
          --paper:#ffffff; --side:#eff6ff; --border:#bfdbfe; --dash:#dbeafe;
        }
        .template-page[data-theme="orange"] {
          --accent:#f97316; --ink:#1a1a1a; --muted:#6b7280;
          --paper:#ffffff; --side:#fff7ed; --border:#fdba74; --dash:#ffe1c3;
        }
        .template-page[data-theme="teal"] {
          --accent:#0ea5a4; --ink:#0f172a; --muted:#5b7a85;
          --paper:#ffffff; --side:#f0fdfa; --border:#99f6e4; --dash:#c6f7f1;
        }
        .template-page[data-theme="forest"] {
          --accent:#22c55e; --ink:#10231b; --muted:#5f776e;
          --paper:#ffffff; --side:#ecfdf5; --border:#a7f3d0; --dash:#c7f0de;
        }
        .template-page[data-theme="rose"] {
          --accent:#e11d48; --ink:#2b0a13; --muted:#a36a7a;
          --paper:#ffffff; --side:#fff1f2; --border:#fecdd3; --dash:#ffd7df;
        }
        .template-page[data-theme="indigo"] {
          --accent:#4f46e5; --ink:#1e1b4b; --muted:#717ab9;
          --paper:#ffffff; --side:#eef2ff; --border:#c7d2fe; --dash:#dbe1ff;
        }
        .template-page[data-theme="malibaara"] {
          --accent:#2379B8; --ink:#0f172a; --muted:#6b7280;
          --paper:#ffffff; --side:#f1f8ff; --border:#cbd5e1; --dash:#dbeafe;
        }

        /********************************************************************************
         LAYOUT PRINCIPAL — scoping strict
        ********************************************************************************/
        .tpl-darkHeader {
          width: 210mm;
          min-height: 297mm;
          margin: 12px auto;
          background: var(--paper);
          border: 1px solid var(--border);
          box-shadow: 0 10px 30px rgba(0,0,0,.15);
          font-family: Arial, sans-serif;
          display: flex;
          flex-direction: column;
        }

        body { background:#222; }

        /* =================== HEADER =================== */
        .hdr {
          background: #000;
          color: #fff;
          padding: 28px 36px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
        }

        .hdr .name {
          font-size: 36px;
          font-weight: 900;
          letter-spacing: 3px;
          text-transform: uppercase;
        }

        .avatar-wrap {
          width: 130px;
          height: 130px;
          border-radius: 50%;
          overflow: hidden;
          border: 4px solid var(--accent);
          background: #fff;
        }
        .avatar-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display:block;
        }

        /* =================== BODY =================== */
        .body-grid {
          display: grid;
          grid-template-columns: 260px 1fr;
          gap: 28px;
          padding: 28px 36px;
        }

        /* LEFT */
        .left {
          color: var(--ink);
        }
        .left .title {
          font-size: 14px;
          font-weight: 900;
          margin-bottom: 8px;
        }
        .left .line {
          width: 100%;
          height: 1px;
          background: var(--border);
          margin: 8px 0 12px;
        }
        .left .kv {
          font-size: 13px;
          line-height: 1.55;
        }
        .left .kv div {
          margin-bottom: 4px;
        }
        .left .skills li {
          margin-bottom: 5px;
          font-size: 13px;
        }

        /* LANGUES */
        .lang-item { margin-bottom: 14px; }
        .lang-head { display:flex; justify-content:space-between; font-size:13px; }
        .lang-note { color: var(--muted); }
        .bar { height: 8px; background:#e5e7eb; border-radius:999px; overflow:hidden; margin-top:5px; }
        .bar > span { display:block; height:100%; background: var(--accent); }

        /* RIGHT COLUMN */
        .right .section + .section { margin-top: 22px; }
        .right .section-title {
          font-weight: 900;
          font-size: 16px;
          color: var(--ink);
          margin-bottom: 6px;
        }
        .right .section-rule {
          width: 100%;
          height: 2px;
          background: var(--accent);
          margin-bottom: 10px;
        }
        .para { font-size: 13px; line-height: 1.6; }

        /* Job */
        .job { margin-bottom: 12px; }
        .job .head { font-weight: 800; color: var(--ink); }
        .job .sub { font-size: 12px; color: var(--muted); margin-top: 2px; }
        .job ul { margin: 6px 0 0 18px; }
        .job li { font-size: 13px; margin: 3px 0; list-style: disc; color:var(--ink); }

        .edu-item { margin-bottom: 10px; }
        .interest-list { margin:0; padding-left:16px; }
        .interest-list li { font-size:13px; margin:4px 0; }
      `}</style>

      <div className="tpl-darkHeader">
        {/* ================= HEADER ================= */}
        <header className="hdr">
          <div className="name">
            {candidate?.firstName} {candidate?.lastName}
          </div>
          <div className="avatar-wrap">
            <img
              crossOrigin="anonymous"
              src={candidate?.profilePictureUrl || "/assets/images/avatar/avatar2.png"}
              alt=""
            />
          </div>
        </header>

        {/* ================= 2-COLUMN GRID ================= */}
        <div className="body-grid">
          {/* LEFT COLUMN */}
          <aside className="left">
            {/* Coordonnées */}
            <div className="title">Coordonnées</div>
            <div className="line"></div>
            <div className="kv">
              {candidate?.address && <div>{candidate.address}</div>}
              {candidate?.phoneNumber1 && <div>{candidate.phoneNumber1}</div>}
              {candidate?.email && <div><a href={`mailto:${candidate.email}`}>{candidate.email}</a></div>}
            </div>

            {/* Compétences */}
            {!!skills.length && (
              <>
                <div className="title" style={{ marginTop: 20 }}>Compétences</div>
                <div className="line"></div>
                <ul className="skills">
                  {skills.map((s, i) => <li key={i}>{s.title}</li>)}
                </ul>
              </>
            )}

            {/* Langues */}
            {!!languages.length && (
              <>
                <div className="title" style={{ marginTop: 20 }}>Langue</div>
                <div className="line"></div>
                {languages.map((l, i) => {
                  return (
                    <div key={i} className="lang-item">
                      <div className="lang-head">
                        <span>{l?.name} - Écrit</span>
                        <span className="lang-note">{l?.writingLevel}</span>
                      </div>
                      <div className="bar"><span style={{ width: l?.writingLevel * 10 + "%" }} /></div>
                       <div className="lang-head" style={{marginTop: "10px"}}>
                        <span>{l?.name} - Parlé</span>
                        <span className="lang-note">{l?.speakingLevel}</span>
                      </div>
                      <div className="bar"><span style={{ width: l?.speakingLevel * 10 + "%" }} /></div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Centres d'intérêt */}
            {!!interests.length && (
              <>
                <div className="title" style={{ marginTop: 20 }}>Centres d’Intérêt</div>
                <div className="line"></div>
                <ul className="interest-list">
                  {interests.map((it, i) => <li key={i}>{it}</li>)}
                </ul>
              </>
            )}
          </aside>

          {/* RIGHT COLUMN */}
          <main className="right">
            {/* Profil professionnel */}
            <section className="section">
              <div className="section-title">Profil Professionnel</div>
              <div className="section-rule"></div>
              <div className="para"
                dangerouslySetInnerHTML={{ __html: candidate?.summaryDescription || "" }}
              />
            </section>

            {/* Parcours professionnel */}
            {!!work.length && (
              <section className="section">
                <div className="section-title">Parcours Professionnel</div>
                <div className="section-rule"></div>
                {work.map((job, i) => (
                  <div className="job" key={i}>
                    <div className="head">{job?.title}</div>
                    <div className="sub">
                      {job?.company ? `${job.company} | ` : ""}
                      {formatRange(job?.startDate, job?.endDate)}
                    </div>
                    {job?.description && <div className="para">{job.description}</div>}
                    {Array.isArray(job?.achievements) && job.achievements.length > 0 && (
                      <ul>{job.achievements.map((a, k) => <li key={k}>{a}</li>)}</ul>
                    )}
                  </div>
                ))}
              </section>
            )}

            {/* Formation */}
            {!!educations.length && (
              <section className="section">
                <div className="section-title">Formation</div>
                <div className="section-rule"></div>
                {educations.map((edu, i) => (
                  <div key={i} className="edu-item">
                    <div className="head">{edu?.degree}</div>
                    <div className="sub">
                      {edu?.institution}
                      {edu?.description} {(edu?.startYear || edu?.endYear) &&
                        ` — ${edu?.startYear || ""}${edu?.endYear ? ` – ${edu.endYear}` : ""}`}
                    </div>
                  </div>
                ))}
              </section>
            )}

            {/* Reference */}
            {!!references.length && (
              <section className="section">
                <div className="section-title">Références</div>
                <div className="section-rule"></div>
                {references.map((ref, i) => (
                  <div className="job" key={i}>
                    <div className="head">{ref?.fullName}</div>
                    <div className="sub">
                      {ref?.title}
                    </div>
                    <div className="para">
                      Tel: {ref.phoneNumber1} / {ref.phoneNumber2} <br />
                      Email: {ref.email}
                    </div>
                  </div>
                ))}
              </section>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default withCandidate(TemplateBody, "Simple Deux Colonnes 3");
